// v10/src/components/AboutPage.js
// A3 — Página "Acerca de Condor360" / Limitaciones del sistema
import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Info, AlertTriangle, FileText, Mail, Table as TableIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import api from '../services/apiService';
import { APP_VERSION, BUILD_COMMIT, BUILD_DATE } from '../version';

// ── Sub-componentes — definidos FUERA para evitar remount ─────────
const SectionTitle = ({ children, darkMode }) => (
  <p style={{
    margin: '0 0 14px', fontSize: 11, fontWeight: 800, textTransform: 'uppercase',
    letterSpacing: '0.1em', color: darkMode ? 'rgba(255,255,255,0.3)' : '#94a3b8',
  }}>
    {children}
  </p>
);

const SectionHeader = ({ icon: Icon, title, darkMode, accent }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
    {Icon && (
      <div style={{
        width: 32, height: 32, borderRadius: 10,
        background: accent ? 'rgba(245,158,11,0.12)' : (darkMode ? 'rgba(37,99,235,0.15)' : 'rgba(37,99,235,0.08)'),
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon style={{ width: 16, height: 16, color: accent ? '#f59e0b' : '#2563eb' }} />
      </div>
    )}
    <p style={{
      margin: 0, fontSize: 14, fontWeight: 800,
      color: darkMode ? 'white' : '#0f172a',
    }}>
      {title}
    </p>
  </div>
);

const KVRow = ({ label, value, darkMode }) => {
  const sub = darkMode ? 'rgba(255,255,255,0.5)' : '#64748b';
  const text = darkMode ? 'white' : '#0f172a';
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, padding: '8px 0', borderBottom: darkMode ? '1px solid rgba(255,255,255,0.04)' : '1px solid #f1f5f9' }}>
      <span style={{ fontSize: 12, fontWeight: 700, color: sub, letterSpacing: '0.02em', flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 13, color: text, textAlign: 'right', wordBreak: 'break-word' }}>{value}</span>
    </div>
  );
};

const AboutPage = ({ darkMode, onBack, currentUser }) => {
  const { t, i18n } = useTranslation('about');
  const [backendVersion, setBackendVersion] = useState(null);
  const [backendError, setBackendError] = useState(false);

  const text   = darkMode ? 'white'                    : '#0f172a';
  const sub    = darkMode ? 'rgba(255,255,255,0.5)'    : '#64748b';
  const cardBg = darkMode ? 'rgba(255,255,255,0.04)'   : 'white';
  const border = darkMode ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e2e8f0';
  const faint  = darkMode ? 'rgba(255,255,255,0.3)'    : '#94a3b8';

  useEffect(() => {
    let cancelled = false;
    api.get('/api/version')
      .then((res) => {
        if (cancelled) return;
        setBackendVersion(res?.data || null);
      })
      .catch(() => {
        if (cancelled) return;
        setBackendError(true);
        setBackendVersion(null);
      });
    return () => { cancelled = true; };
  }, []);

  const formatDate = (iso) => {
    if (!iso) return '—';
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(i18n.language === 'en' ? 'en-US' : 'es-AR', {
        year: 'numeric', month: '2-digit', day: '2-digit',
      });
    } catch {
      return iso;
    }
  };

  const tableRows = [
    { fn: 'fn_checklist',          type: 'type_admin_assistance',       src: 'source_aeroclub_templates'    },
    { fn: 'fn_logbook',            type: 'type_complementary_record',   src: 'source_pilot_input'           },
    { fn: 'fn_squawks',            type: 'type_internal_communication', src: 'source_pilots_mechanics'      },
    { fn: 'fn_horometer',          type: 'type_complementary_tracking', src: 'source_manual_input'          },
    { fn: 'fn_voice',              type: 'type_reading_assistant',      src: 'source_elevenlabs'            },
    { fn: 'fn_suppliers',          type: 'type_admin_assistance',       src: 'source_aeroclub_input'        },
    { fn: 'fn_digital_signature',  type: 'type_complementary_record',   src: 'source_user_touch'            },
    { fn: 'fn_reports',            type: 'type_management_summary',     src: 'source_system_aggregation'    },
  ];

  const limitations = [
    'limitation_not_poh',
    'limitation_not_certified',
    'limitation_administrative',
    'limitation_pic_responsibility',
    'limitation_wb_validation',
    'limitation_see_tc',
  ];

  const cardStyle = { borderRadius: 18, background: cardBg, border, padding: '20px' };

  return (
    <motion.div
      initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 40 }}
      transition={{ type: 'spring', stiffness: 160, damping: 20 }}
      style={{ minHeight: '100vh', background: darkMode ? '#0a0f1e' : '#f8fafc', overflowY: 'auto' }}
    >
      {/* Header sticky */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 10,
        background: darkMode ? 'rgba(10,15,30,0.95)' : 'rgba(248,250,252,0.95)',
        backdropFilter: 'blur(20px)', borderBottom: border,
        padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 12,
      }}>
        <motion.button onClick={onBack} whileTap={{ scale: 0.95 }}
          aria-label={t('back_button')}
          style={{
            width: 38, height: 38, borderRadius: 11, border, background: 'transparent',
            cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: sub,
          }}>
          <ArrowLeft style={{ width: 18, height: 18 }} />
        </motion.button>
        <div style={{ minWidth: 0 }}>
          <p style={{ margin: 0, fontSize: 17, fontWeight: 800, color: text }}>{t('page_title')}</p>
          {currentUser?.organization_name && (
            <p style={{ margin: 0, fontSize: 12, color: sub, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {currentUser.organization_name}
            </p>
          )}
        </div>
      </div>

      {/* Contenido */}
      <div style={{ maxWidth: 640, margin: '0 auto', padding: '24px 20px', display: 'flex', flexDirection: 'column', gap: 20 }}>

        {/* SECCIÓN 1 — Versión y build */}
        <div style={cardStyle}>
          <SectionTitle darkMode={darkMode}>{t('section_version')}</SectionTitle>

          <p style={{ margin: '0 0 6px', fontSize: 12, fontWeight: 700, color: sub }}>{t('frontend_label')}</p>
          <KVRow darkMode={darkMode} label="v" value={APP_VERSION} />
          <KVRow darkMode={darkMode} label={t('commit_label')} value={(BUILD_COMMIT || 'dev').slice(0, 7)} />
          <KVRow darkMode={darkMode} label={t('build_label')} value={BUILD_DATE} />

          <p style={{ margin: '14px 0 6px', fontSize: 12, fontWeight: 700, color: sub }}>{t('backend_label')}</p>
          {backendVersion ? (
            <>
              <KVRow darkMode={darkMode} label="v" value={backendVersion.version || '—'} />
              <KVRow darkMode={darkMode} label={t('commit_label')} value={(backendVersion.commit || 'dev').slice(0, 7)} />
              <KVRow darkMode={darkMode} label={t('deployed_label')} value={formatDate(backendVersion.deployed_at)} />
            </>
          ) : (
            <p style={{ margin: '6px 0 0', fontSize: 13, color: faint, fontStyle: 'italic' }}>
              {backendError ? t('backend_not_available') : '…'}
            </p>
          )}
        </div>

        {/* SECCIÓN 2 — Información del producto */}
        <div style={cardStyle}>
          <SectionHeader icon={Info} title={t('section_product')} darkMode={darkMode} />
          <KVRow darkMode={darkMode} label={t('product_name_label')} value="Condor360" />
          <KVRow darkMode={darkMode} label={t('pilot_client_label')} value={t('pilot_client_value')} />
          <KVRow darkMode={darkMode} label={t('start_year_label')} value={t('start_year_value')} />
          <KVRow darkMode={darkMode} label={t('developed_in_label')} value={t('developed_in_value')} />
          <p style={{ margin: '14px 0 0', fontSize: 12, fontWeight: 700, color: sub, letterSpacing: '0.02em' }}>
            {t('product_description_label')}
          </p>
          <p style={{ margin: '6px 0 0', fontSize: 13, color: text, lineHeight: 1.55 }}>
            {t('product_description')}
          </p>
        </div>

        {/* SECCIÓN 3 — Limitaciones (DESTACADO) */}
        <div style={{
          borderRadius: 18,
          background: darkMode ? 'rgba(245,158,11,0.06)' : 'rgba(254,243,199,0.55)',
          border: '1.5px solid rgba(245,158,11,0.45)',
          padding: '20px',
        }}>
          <SectionHeader icon={AlertTriangle} title={t('section_limitations')} darkMode={darkMode} accent />
          <p style={{ margin: '0 0 12px', fontSize: 13, color: text, lineHeight: 1.55, fontWeight: 600 }}>
            {t('limitations_intro')}
          </p>
          <ul style={{ margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 10 }}>
            {limitations.map((key) => (
              <li key={key} style={{ fontSize: 13, color: text, lineHeight: 1.55 }}>
                {t(key)}
              </li>
            ))}
          </ul>
        </div>

        {/* SECCIÓN 4 — Links legales */}
        <div style={cardStyle}>
          <SectionHeader icon={FileText} title={t('section_legal')} darkMode={darkMode} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <a
              href={t('tc_link_url')}
              target="_blank" rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px', borderRadius: 12,
                background: darkMode ? 'rgba(37,99,235,0.10)' : 'rgba(37,99,235,0.06)',
                border: '1px solid rgba(37,99,235,0.25)',
                color: '#2563eb', fontSize: 13, fontWeight: 700, textDecoration: 'none',
              }}>
              <FileText style={{ width: 16, height: 16 }} />
              {t('tc_link_label')}
            </a>
            <a
              href={t('privacy_link_url')}
              target="_blank" rel="noopener noreferrer"
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px', borderRadius: 12,
                background: darkMode ? 'rgba(37,99,235,0.10)' : 'rgba(37,99,235,0.06)',
                border: '1px solid rgba(37,99,235,0.25)',
                color: '#2563eb', fontSize: 13, fontWeight: 700, textDecoration: 'none',
              }}>
              <FileText style={{ width: 16, height: 16 }} />
              {t('privacy_link_label')}
            </a>
            <a
              href={`mailto:${t('support_email')}`}
              style={{
                display: 'flex', alignItems: 'center', gap: 10,
                padding: '12px 14px', borderRadius: 12,
                background: darkMode ? 'rgba(255,255,255,0.03)' : '#f8fafc',
                border,
                color: text, fontSize: 13, fontWeight: 600, textDecoration: 'none',
              }}>
              <Mail style={{ width: 16, height: 16, color: sub }} />
              <span style={{ color: sub, fontWeight: 700 }}>{t('support_label')}:</span>
              <span>{t('support_email')}</span>
            </a>
          </div>
        </div>

        {/* SECCIÓN 5 — Tabla funciones / tipos / fuentes */}
        <div style={cardStyle}>
          <SectionHeader icon={TableIcon} title={t('section_functions_table')} darkMode={darkMode} />
          <p style={{ margin: '0 0 14px', fontSize: 13, color: sub, lineHeight: 1.55 }}>
            {t('table_intro')}
          </p>
          <div style={{ overflowX: 'auto', borderRadius: 12, border }}>
            <table style={{
              width: '100%', borderCollapse: 'collapse', fontSize: 12,
            }}>
              <thead>
                <tr style={{ background: darkMode ? 'rgba(255,255,255,0.04)' : '#f8fafc' }}>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 800, color: sub, letterSpacing: '0.04em', fontSize: 11, textTransform: 'uppercase' }}>{t('table_function')}</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 800, color: sub, letterSpacing: '0.04em', fontSize: 11, textTransform: 'uppercase' }}>{t('table_type')}</th>
                  <th style={{ textAlign: 'left', padding: '10px 12px', fontWeight: 800, color: sub, letterSpacing: '0.04em', fontSize: 11, textTransform: 'uppercase' }}>{t('table_source')}</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, idx) => (
                  <tr key={row.fn} style={{ borderTop: darkMode ? '1px solid rgba(255,255,255,0.05)' : '1px solid #f1f5f9', background: idx % 2 === 1 ? (darkMode ? 'rgba(255,255,255,0.015)' : '#fafbfc') : 'transparent' }}>
                    <td style={{ padding: '10px 12px', color: text, fontWeight: 600 }}>{t(row.fn)}</td>
                    <td style={{ padding: '10px 12px', color: text }}>{t(row.type)}</td>
                    <td style={{ padding: '10px 12px', color: sub }}>{t(row.src)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Footer */}
        <p style={{ margin: '8px 0 24px', fontSize: 12, color: faint, textAlign: 'center' }}>
          {t('footer')}
        </p>
      </div>
    </motion.div>
  );
};

export default AboutPage;
